import React, { useMemo, useState } from "react";
import {
  Table,
  DatePicker,
  Select,
  Card,
  Row,
  Col,
  Tag,
  Drawer,
  Statistic,
  Avatar,
  Collapse,
} from "antd";
import dayjs from "dayjs";
import useData from "../../hooks/useData";

const { RangePicker } = DatePicker;
const { Panel } = Collapse;

/* ================= CONFIG ================= */

const DESIGNER_PERCENT = 0.1;
const TEAM_PERCENT = 0.15;

/* ================= SAFE HELPERS ================= */

const safeArray = (a) => (Array.isArray(a) ? a : []);
const safeIncludes = (a, id) => Array.isArray(a) && a.includes(id);

const getColorFromInitials = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 70%, 50%)`;
};

/* ================= PAGE ================= */

export default function PayrollPage() {
  const { data: contracts = [], isLoading } = useData("/sales/contracts");
  const { data: teams = [] } = useData("/products/users");
  const { data: designers = [] } = useData("/products/designers");
  const { data: designerPaid = [] } = useData("/products/designerPaid", []);
  const { data: teamPaid = [] } = useData("/products/teamPaid", []);

  const [range, setRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [employeeId, setEmployeeId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  /* ================= EMPLOYEES ================= */

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getName = (id) => {
    return teams?.find((user) => user._id === id)?.name;
  };

  const employees = useMemo(() => {
    const map = {};

    // Designers — use d.id (not _id)
    (designers || []).forEach((d) => {
      if (!d?.id) return;
      map[d.id] = {
        id: d.id,
        name: getName(d.id) || "Designer",
        type: "designer",
      };
    });

    // Team — use user._id
    (teams || []).forEach((t) => {
      const id = t?._id;
      if (!id) return;

      // Do NOT override designers
      if (map[id]) return;

      map[id] = {
        id,
        name: t.name || t.fullName || t.username || "Team",
        type: "team",
      };
    });

    return Object.values(map);
  }, [designers, getName, teams]);

  /* ================= QUICK MONTHS ================= */

  const quickMonths = Array.from({ length: 6 }).map((_, i) => {
    const m = dayjs().subtract(i, "month");
    return {
      label: m.format("MMMM YYYY"),
      range: [m.startOf("month"), m.endOf("month")],
    };
  });

  /* ================= PAYMENTS ================= */

  const allPayments = useMemo(
    () => [
      ...designerPaid.map((p) => ({ ...p, type: "designer" })),
      ...teamPaid.map((p) => ({ ...p, type: "team" })),
    ],
    [designerPaid, teamPaid],
  );

  /* ================= PEOPLE CARDS (REAL PAID) ================= */

  const safeId = (v) => String(v || "");

  const people = useMemo(() => {
    const map = {};

    allPayments.forEach((p) => {
      const date = dayjs(p.date);
      if (!date.isValid()) return;
      if (date.isBefore(range[0]) || date.isAfter(range[1])) return;

      const contract = contracts.find(
        (c) => safeId(c._id) === safeId(p.contractId),
      );
      if (!contract) return;

      const targets =
        p.type === "designer"
          ? safeArray(contract.designer)
          : safeArray(contract.team);

      targets.forEach((empId) => {
        const id = safeId(empId);

        if (!map[id]) map[id] = { id, type: p.type, paid: 0 };
        map[id].paid += Number(p.amount || 0);
      });
    });

    return Object.values(map);
  }, [allPayments, contracts, range]);

  console.log("emp", employees);
  console.log("people", people);

  /* ================= LEDGER ================= */

  const ledger = useMemo(() => {
    const rows = [];

    allPayments.forEach((p) => {
      const date = dayjs(p.date);
      if (!date.isValid()) return;
      if (date.isBefore(range[0]) || date.isAfter(range[1])) return;

      const contract = contracts.find((c) => c._id === p.contractId);
      if (!contract) return;

      const targets =
        p.type === "designer"
          ? safeArray(contract.designer)
          : safeArray(contract.team);

      targets.forEach((empId) => {
        if (employeeId && empId !== employeeId) return;

        const emp = employees.find((e) => e.id === empId);
        if (!emp) return;

        rows.push({
          key: p._id + empId,
          date: p.date,
          employee: emp,
          contract: contract.id || contract.number || contract._id,
          payment: Number(p.amount || 0),
        });
      });
    });

    return rows.sort((a, b) => dayjs(b.date) - dayjs(a.date));
  }, [contracts, allPayments, employees, range, employeeId]);

  return (
    <div>
      <div className="page-wrapper" style={{ padding: "50px 0" }}>
        <div className="content">
          {/* Quick months */}
          <Row gutter={8} style={{ marginBottom: 12 }}>
            {quickMonths.map((m) => (
              <Col key={m.label}>
                <Tag
                  style={{ cursor: "pointer" }}
                  color="blue"
                  onClick={() => setRange(m.range)}
                >
                  {m.label}
                </Tag>
              </Col>
            ))}
          </Row>

          {/* Filters */}
          <Card>
            <Row gutter={12}>
              <Col xs={24} md={10}>
                <RangePicker
                  size="large"
                  value={range}
                  onChange={setRange}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} md={10}>
                <Select
                  size="large"
                  showSearch
                  allowClear
                  placeholder="Employee"
                  style={{ width: "100%" }}
                  value={employeeId}
                  onChange={setEmployeeId}
                  optionFilterProp="label"
                  options={employees.map((e) => ({
                    value: e.id,
                    label: e.name,
                  }))}
                  optionRender={(option) => {
                    const name = option.label;
                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Avatar
                          size="small"
                          style={{
                            backgroundColor: getColorFromInitials(name),
                            color: "white",
                          }}
                        >
                          {name?.[0]}
                        </Avatar>
                        <span>{name}</span>
                      </div>
                    );
                  }}
                />
              </Col>
            </Row>
          </Card>

          {/* People cards */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            {people.map((p) => {
              const emp = employees.find((e) => e.id === p.id);
              return (
                <Col xs={12} md={6} key={p.id}>
                  <Card hoverable onClick={() => setEmployeeId(p.id)}>
                    <b>{emp?.name}</b>
                    <div>
                      <Tag color={p.type === "designer" ? "blue" : "orange"}>
                        {p.type}
                      </Tag>
                    </div>
                    <h3>{p.paid.toFixed(2)} ₾</h3>
                    <small>Paid this period</small>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* Ledger */}
          <Table
            size="large"
            loading={isLoading}
            style={{ marginTop: 20 }}
            dataSource={ledger}
            scroll={{ x: 900 }}
            onRow={(r) => ({
              onClick: () => setSelectedEmployee(r.employee),
            })}
            columns={[
              { title: "Date", dataIndex: "date" },
              { title: "Employee", render: (r) => r.employee.name },
              { title: "Contract", dataIndex: "contract" },
              { title: "Payment", dataIndex: "payment" },
            ]}
          />

          {/* Sidebar */}
          <Drawer
            open={!!selectedEmployee}
            onClose={() => setSelectedEmployee(null)}
            title={selectedEmployee?.name}
            width={520}
          >
            {selectedEmployee && (
              <EmployeeProfile
                employee={selectedEmployee}
                contracts={contracts}
                payments={allPayments}
              />
            )}
          </Drawer>
        </div>
      </div>
    </div>
  );
}

/* ================= SIDEBAR ================= */

function EmployeeProfile({ employee, contracts, payments }) {
  const empContracts = contracts.filter((c) => {
    if (employee.type === "designer")
      return safeIncludes(c.designer, employee.id);
    return safeIncludes(c.team, employee.id);
  });

  const empPayments = payments.filter((p) => {
    const c = contracts.find((x) => x._id === p.contractId);
    if (!c) return false;
    return employee.type === "designer"
      ? safeIncludes(c.designer, employee.id)
      : safeIncludes(c.team, employee.id);
  });

  const monthly = useMemo(() => {
    const map = {};
    empPayments.forEach((p) => {
      const m = dayjs(p.date).format("YYYY-MM");
      if (!map[m]) map[m] = [];
      map[m].push(p);
    });
    return map;
  }, [empPayments]);

  const totalPaid = empPayments.reduce((s, p) => s + Number(p.amount || 0), 0);

  const totalEarned = empContracts.reduce((s, c) => {
    const price = Number(c.price || 0);
    return (
      s +
      (employee.type === "designer"
        ? price * DESIGNER_PERCENT
        : (price * TEAM_PERCENT) / (safeArray(c.team).length || 1))
    );
  }, 0);

  return (
    <>
      <Row gutter={16}>
        <Col span={8}>
          <Statistic title="Earned" value={totalEarned.toFixed(2)} suffix="₾" />
        </Col>
        <Col span={8}>
          <Statistic title="Paid" value={totalPaid.toFixed(2)} suffix="₾" />
        </Col>
        <Col span={8}>
          <Statistic
            title="Debt"
            value={(totalEarned - totalPaid).toFixed(2)}
            suffix="₾"
          />
        </Col>
      </Row>

      <Collapse style={{ marginTop: 20 }}>
        {Object.entries(monthly).map(([month, list]) => {
          const sum = list.reduce((s, p) => s + Number(p.amount || 0), 0);
          return (
            <Panel header={`${month} — ${sum.toFixed(2)} ₾`} key={month}>
              <Table
                size="large"
                dataSource={list.map((p) => {
                  const c = contracts.find((x) => x._id === p.contractId);
                  return {
                    key: p._id,
                    date: p.date,
                    contract: c?.id || c?.number || p.contractId,
                    amount: p.amount,
                  };
                })}
                columns={[
                  { title: "Date", dataIndex: "date" },
                  { title: "Contract", dataIndex: "contract" },
                  { title: "Paid", dataIndex: "amount" },
                ]}
                pagination={false}
              />
            </Panel>
          );
        })}
      </Collapse>
    </>
  );
}
